import {getCurrentUser} from "aws-amplify/auth";
import {Hub} from "aws-amplify/utils";
import type React from "react";
import {useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

type SessionState = "checking" | "present" | "absent";

/**
 * Access control is session presence and nothing more. There is no authenticated-but-unauthorized state
 * to handle: the admin API gateway trusts only the Identity Center-backed admin pool, so either you hold
 * a token from it or you never got one.
 */
export function RequireAdminSession(props: {children: React.ReactNode}) {
    const [session, setSession] = useState<SessionState>("checking");

    useEffect(() => {
        let active = true;
        getCurrentUser()
                .then(() => {
                    if (active) setSession("present");
                })
                .catch(() => {
                    if (active) setSession("absent");
                });
        return () => {
            active = false;
        };
    }, []);

    // Signing out has to unmount the protected tree, not merely invalidate the next API call. Without
    // this the operator stays looking at cross-tenant data they have just signed out of — on a shared
    // machine, for as long as the tab is open.
    useEffect(() => {
        return Hub.listen("auth", ({payload}) => {
            if (payload.event === "signedOut") {
                setSession("absent");
            }
        });
    }, []);

    if (session === "absent") {
        return <Navigate to="/login" replace />;
    }

    if (session === "checking") {
        return (
                <Box sx={{p: 3}} data-testid="app-loading-skeleton">
                    <Skeleton width={200} height={32} sx={{mb: 2}} />
                    <Skeleton width="80%" height={24} sx={{mb: 1}} />
                    <Skeleton width="60%" height={24} />
                </Box>
        );
    }

    return <>{props.children}</>;
}
